import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import WorkOrders from './pages/WorkOrders';
import Spaces from './pages/Spaces';
import Maintenance from './pages/Maintenance';
import Users from './pages/Users';
import Settings from './pages/Settings';
import MyTasks from './pages/MyTasks';
import Reports from './pages/Reports';
import PublicBooking from './pages/PublicBooking';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import LegalPage from './pages/LegalPage';
import BookingPage from './pages/BookingPage';
import ReportPage from './pages/ReportPage';
import ContactPage from './pages/ContactPage';
import Bookings from './pages/Bookings';
import Inventory from './pages/Inventory';
import Messages from './pages/Messages';
import VendorQuote from './pages/VendorQuote';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Developer from './pages/Developer';
import SignUp from './pages/SignUp';
import VerifyEmail from './pages/VerifyEmail';
import Billing from './pages/Billing';
import BillingResult from './pages/BillingResult';

function getUser() {
  return JSON.parse(localStorage.getItem('fms_user') || '{}');
}

function PrivateRoute({ children }) {
  return localStorage.getItem('fms_token') ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <Navigate to="/login" replace />;
  return ['admin', 'developer'].includes(getUser().role) ? children : <Navigate to="/app/dashboard" replace />;
}

function ManagerRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <Navigate to="/login" replace />;
  const role = getUser().role;
  return (role === 'admin' || role === 'manager') ? children : <Navigate to="/app/my-tasks" replace />;
}

function DeveloperRoute({ children }) {
  if (!localStorage.getItem('fms_token')) return <Navigate to="/login" replace />;
  return getUser().role === 'developer' ? children : <Navigate to="/app/dashboard" replace />;
}

// Short public link for an organization: /o/acme-co opens its public booking page,
// /o/acme-co/report its report page, etc. (same as /?system=acme-co, /report?system=acme-co).
const ORG_PAGES = ['about', 'services', 'booking', 'report', 'contact', 'login', 'signup'];
function OrgRedirect() {
  const { slug, page } = useParams();
  const path = page && ORG_PAGES.includes(page) ? `/${page}` : '/';
  window.location.replace(`${path}?system=${encodeURIComponent(slug || '')}`);
  return null;
}

function AppDefaultRoute() {
  const token = localStorage.getItem('fms_token');
  if (!token) return <PublicBooking />;
  const role = getUser().role;
  return role === 'employee'
    ? <Navigate to="/app/my-tasks" replace />
    : role === 'developer' ? <Navigate to="/app/developer" replace />
    : <Navigate to="/app/dashboard" replace />;
}

export default function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          {/* Public — no login required */}
          <Route path="/" element={<AppDefaultRoute />} />
          <Route path="/o/:slug" element={<OrgRedirect />} />
          <Route path="/o/:slug/:page" element={<OrgRedirect />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
          <Route path="/terms-of-service" element={<LegalPage type="terms" />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/report" element={<ReportPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/billing/result" element={<BillingResult />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/vendor/asset-quote/:token" element={<VendorQuote />} />

          {/* Authenticated app */}
          <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<ManagerRoute><Dashboard /></ManagerRoute>} />
            <Route path="assets" element={<ManagerRoute><Assets /></ManagerRoute>} />
            <Route path="work-orders" element={<ManagerRoute><WorkOrders /></ManagerRoute>} />
            <Route path="spaces" element={<ManagerRoute><Spaces /></ManagerRoute>} />
            <Route path="maintenance" element={<ManagerRoute><Maintenance /></ManagerRoute>} />
            <Route path="bookings" element={<ManagerRoute><Bookings /></ManagerRoute>} />
            <Route path="inventory" element={<ManagerRoute><Inventory /></ManagerRoute>} />
            <Route path="messages" element={<Messages />} />
            <Route path="reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="my-tasks" element={<PrivateRoute><MyTasks /></PrivateRoute>} />
            <Route path="users" element={<PrivateRoute><Users /></PrivateRoute>} />
            <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
            <Route path="developer" element={<DeveloperRoute><Developer /></DeveloperRoute>} />
          </Route>

          {/* Legacy short paths */}
          <Route path="/dashboard" element={<PrivateRoute><Navigate to="/app/dashboard" replace /></PrivateRoute>} />
          <Route path="/my-tasks" element={<PrivateRoute><Navigate to="/app/my-tasks" replace /></PrivateRoute>} />
          <Route path="/assets" element={<PrivateRoute><Navigate to="/app/assets" replace /></PrivateRoute>} />
          <Route path="/work-orders" element={<PrivateRoute><Navigate to="/app/work-orders" replace /></PrivateRoute>} />
          <Route path="/spaces" element={<PrivateRoute><Navigate to="/app/spaces" replace /></PrivateRoute>} />
          <Route path="/maintenance" element={<PrivateRoute><Navigate to="/app/maintenance" replace /></PrivateRoute>} />
          <Route path="/bookings" element={<PrivateRoute><Navigate to="/app/bookings" replace /></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><Navigate to="/app/inventory" replace /></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><Navigate to="/app/messages" replace /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><Navigate to="/app/reports" replace /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><Navigate to="/app/users" replace /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Navigate to="/app/settings" replace /></PrivateRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}
